import type { AdminPermission } from '@carcheck/shared';
import { useMemo } from 'react';

import { useAuthStore } from '../stores';

/**
 * Permission helpers reading from the MobX auth store
 * (`auth.store.currentAdmin.permissions`). TanStack owns server state;
 * the permission set is *session* state, so it lives in MobX (the JWT
 * carries it, surfaced on `currentAdmin`).
 *
 * Fail-closed: when there's no authenticated admin (or the permission
 * list is empty), every check returns `false`. Pages gate render +
 * action visibility on these, never on a tooltip / disabled-but-present
 * button (avoids PII spillage per PLAN §1.1).
 *
 * NB: these hooks read an observable; consumers must be `observer`-wrapped
 * for the value to update reactively on login / permission refresh.
 */

/** The current admin's permission set (empty when logged out). */
export const usePermissions = (): readonly AdminPermission[] => {
  const auth = useAuthStore();
  return auth.currentAdmin?.permissions ?? [];
};

/** True when the admin holds `perm`. */
export const useHasPermission = (perm: AdminPermission): boolean => {
  const permissions = usePermissions();
  return permissions.includes(perm);
};

/** True when the admin holds at least one of `perms`. */
export const useAnyPermission = (perms: readonly AdminPermission[]): boolean => {
  const permissions = usePermissions();
  return useMemo(() => perms.some((p) => permissions.includes(p)), [perms, permissions]);
};

/** True when the admin holds every one of `perms`. */
export const useAllPermissions = (perms: readonly AdminPermission[]): boolean => {
  const permissions = usePermissions();
  return useMemo(() => perms.every((p) => permissions.includes(p)), [perms, permissions]);
};
